// @ts-check
// Primeiros passos no rodapé da gaveta (doc 12, seção 12; contrato 08-contrato-navegacao-mobile.md, seção 3): progresso,
// cada passo leva à tela onde se faz, pode ser pulado, e a lista some quando tudo foi feito ou pulado (ou quando a pessoa
// a fecha). Não bloqueia nada: escolas existentes seguem como estavam.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { locationState, navigate } from '../../app/router.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { loadOnboarding, onboardingState, updateOnboarding } from '../../data/navigation.js';
import { readStorage, writeStorage } from '../../lib/utils.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { SpreadsheetImportDialog } from '../spreadsheet-import/spreadsheet-import-dialog.js';
import { isMobileNavigationOpenState } from './navigation-state.js';
import { t } from '../../i18n/index.js';

const COLLAPSED_STORAGE_KEY = 'appcrm.onboardingCollapsed';

/** Onde cada passo se faz; a importação abre o assistente de famílias ali mesmo. */
const STEP_DESTINATIONS = Object.freeze({
  PROFILE: { icon: 'IconUserCircle', href: '/settings/profile' },
  INVITE_TEAM: { icon: 'IconUserPlus', href: '/settings/members' },
  CONNECT_CHANNEL: { icon: 'IconBrandWhatsapp', href: '/settings/channels' },
  IMPORT_SPREADSHEET: { icon: 'IconUpload', href: null },
  CONNECT_ACCOUNT: { icon: 'IconCalendarEvent', href: '/settings/accounts' },
  BECOME_HOST: { icon: 'IconCalendarPlus', href: '/settings/booking-pages?host=1' },
});

export function OnboardingChecklist() {
  const pathname = locationState.value.pathname;
  // no celular começa recolhida, para não empurrar o menu; depois vale a escolha da pessoa
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = readStorage(COLLAPSED_STORAGE_KEY);
    return stored === null ? window.matchMedia?.('(max-width: 768px)').matches === true : stored === 'true';
  });
  const [isImportOpen, setIsImportOpen] = useState(false);
  // confere o progresso ao abrir e ao trocar de tela (com intervalo mínimo em loadOnboarding)
  useEffect(() => {
    loadOnboarding();
  }, [pathname]);
  const status = onboardingState.value;
  if (!status?.isVisible) return null;
  const importObject = objectsByNameSingular.value.get('household') ?? objectsByNameSingular.value.get('person');
  const progress = status.totalCount > 0 ? Math.round((status.completedCount / status.totalCount) * 100) : 0;
  const toggle = () => {
    setIsCollapsed(!isCollapsed);
    writeStorage(COLLAPSED_STORAGE_KEY, String(!isCollapsed));
  };
  return html`<section class="onboarding-checklist" aria-label=${t('navigationMobile.onboarding.title')} data-testid="onboarding-checklist">
    <div class="onboarding-checklist-header">
      <button type="button" class="onboarding-checklist-toggle" aria-expanded=${isCollapsed ? 'false' : 'true'} onClick=${toggle}>
        <${Icon} name="IconRocket" size=${16} />
        <span class="onboarding-checklist-title">${t('navigationMobile.onboarding.title')}</span>
      </button>
      <${LightIconButton}
        icon="IconX"
        ariaLabel=${t('navigationMobile.onboarding.dismiss')}
        onClick=${() => updateOnboarding({ dismiss: true }).catch(showErrorSnackbar)}
      />
    </div>
    <div class="onboarding-checklist-progress">
      <div class="onboarding-checklist-bar" role="progressbar" aria-valuenow=${progress} aria-valuemin="0" aria-valuemax="100">
        <span style=${{ width: `${progress}%` }}></span>
      </div>
      <span class="onboarding-checklist-count">${t('navigationMobile.onboarding.progress', { done: status.completedCount, total: status.totalCount })}</span>
    </div>
    ${isCollapsed
      ? null
      : html`<ul class="onboarding-checklist-steps">
          ${status.steps.map((step) => {
            const destination = STEP_DESTINATIONS[/** @type {keyof typeof STEP_DESTINATIONS} */ (step.key)];
            const isComplete = step.isDone || step.isSkipped;
            const open = () => {
              isMobileNavigationOpenState.value = false;
              if (destination?.href) navigate(destination.href);
              else if (importObject) setIsImportOpen(true);
            };
            return html`<li key=${step.key} class="onboarding-step" data-complete=${isComplete ? 'true' : undefined}>
              <button type="button" class="onboarding-step-main" onClick=${open} disabled=${step.isDone}>
                <${Icon} name=${step.isDone ? 'IconCircleCheck' : isComplete ? 'IconCircleCheck' : 'IconCircle'} size=${16} className="onboarding-step-check" />
                <span class="onboarding-step-text">
                  <span class="onboarding-step-title">${t(`navigationMobile.onboarding.steps.${step.key}.title`)}</span>
                  ${isComplete ? null : html`<span class="onboarding-step-description">${t(`navigationMobile.onboarding.steps.${step.key}.description`)}</span>`}
                </span>
              </button>
              ${isComplete
                ? null
                : html`<button type="button" class="onboarding-step-skip" onClick=${() => updateOnboarding({ skipStep: step.key }).catch(showErrorSnackbar)}>
                    ${t('navigationMobile.onboarding.skip')}
                  </button>`}
            </li>`;
          })}
        </ul>`}
    ${isImportOpen && importObject
      ? html`<${SpreadsheetImportDialog} objectMetadata=${importObject} onClose=${() => {
          setIsImportOpen(false);
          loadOnboarding({ force: true });
        }} />`
      : null}
  </section>`;
}
